import { useEffect, useMemo, useState } from 'react'
import { earningsRequest } from '../lib/earningsClient.js'
import { resolveEarningsAnchor } from '../lib/chart/earningsVwap.js'

export function useEarningsVwapAnchor(symbol, enabled, userId, tradingCalendar, anchorTiming = 'earnings-date', reportCount = 1) {
  const [snapshot, setSnapshot] = useState(null)
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    if (!enabled) return
    let disposed = false
    let loading = false
    setSnapshot(null)
    const refresh = async () => {
      if (loading || document.visibilityState === 'hidden') return
      loading = true
      setNow(new Date())
      try {
        const result = await earningsRequest(symbol)
        if (!disposed) setSnapshot({ ...result, symbol, userId, status: 'ready' })
      } catch {
        if (!disposed) setSnapshot({ symbol, userId, status: 'unavailable' })
      } finally { loading = false }
    }
    refresh()
    const timer = setInterval(refresh, 60_000)
    window.addEventListener('focus', refresh)
    document.addEventListener('visibilitychange', refresh)
    return () => {
      disposed = true
      clearInterval(timer)
      window.removeEventListener('focus', refresh)
      document.removeEventListener('visibilitychange', refresh)
    }
  }, [symbol, enabled, userId])

  return useMemo(() => {
    if (!enabled) return { status: 'unavailable', message: 'Earnings VWAP requires a live equity chart' }
    if (snapshot?.symbol !== symbol || snapshot?.userId !== userId) return { status: 'loading', message: 'Loading earnings…' }
    if (snapshot.status !== 'ready') return { status: 'unavailable', message: 'Earnings data unavailable' }
    const retrieved = Date.parse(snapshot.retrievedAt)
    if (!Number.isFinite(retrieved) || now.getTime() - retrieved >= 15 * 60_000) return { status: 'unavailable', message: 'Earnings data is stale' }
    return resolveEarningsAnchor(snapshot.events, now, tradingCalendar, anchorTiming, reportCount)
  }, [enabled, snapshot, symbol, userId, now, tradingCalendar, anchorTiming, reportCount])
}
